import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Calendar, Clock, FileText, Plane, CheckCircle, Edit, ShieldCheck, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';
import { generateInitials, generateDisplayName } from '@/utils/avatarUtils';
import { getLeaveTypeLabel } from '@/constants/leaveTypes';
import { formatLeaveDuration, isPartDayRequest } from '@/utils/leaveDurationUtils';

import type { LeavePayrollVerificationStatus } from '@/services/leave/types';

interface LeaveRequest {
  id: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  reason: string | null;
  status: string;
  created_at: string;
  requested_hours?: number | null;
  requested_days?: number | null;
  payroll_status?: LeavePayrollVerificationStatus;
  profile: {
    first_name: string | null;
    last_name: string | null;
    email: string;
    avatar_url: string | null;
  };
}

interface ManagerLeaveCardProps {
  request: LeaveRequest;
  getStatusBadgeVariant: (status: string) => any;
  onStatusUpdate?: (requestId: string, newStatus: 'approved' | 'rejected') => void;
  onEditRequest?: (request: LeaveRequest) => void;
  showActions?: boolean;
}

export function ManagerLeaveCard({
  request,
  getStatusBadgeVariant,
  onStatusUpdate,
  onEditRequest,
  showActions = false,
}: ManagerLeaveCardProps) {
  const isPartDay = isPartDayRequest(request);

  return (
    <Card className="w-full">
      <CardContent className="p-4">
        <div className="space-y-3">
          {/* Employee Info */}
          <div className="flex items-center gap-2">
            <Avatar className="h-10 w-10">
              <AvatarImage 
                src={request.profile?.avatar_url} 
                alt={generateDisplayName(request.profile?.first_name, request.profile?.last_name)}
              />
              <AvatarFallback>
                {generateInitials(request.profile?.first_name, request.profile?.last_name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold">
                {request.profile?.first_name || 'Unknown'} {request.profile?.last_name || 'User'}
              </p>
              <p className="text-sm text-muted-foreground">{request.profile?.email || 'No email'}</p>
            </div>
          </div>

          {/* Leave Type */}
          <div className="flex items-center gap-2">
            <Plane className="h-4 w-4 text-muted-foreground" />
            <span className="font-semibold">{getLeaveTypeLabel(request.leave_type)}</span>
            {isPartDay && (
              <Badge variant="secondary" className="text-xs">Part Day</Badge>
            )}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-muted-foreground">Start Date</span>
              <p className="font-medium">
                {format(new Date(request.start_date), 'MMM d, yyyy')}
              </p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">End Date</span>
              <p className="font-medium">
                {format(new Date(request.end_date), 'MMM d, yyyy')}
              </p>
            </div>
          </div>

          {/* Duration & Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-muted-foreground">Duration</span>
              <div className="flex items-center gap-2 mt-1">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">
                  {formatLeaveDuration(request)}
                </span>
              </div>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Status</span>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Badge variant={getStatusBadgeVariant(request.status)}>
                  {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                </Badge>
                {request.payroll_status === 'verified' && (
                  <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50 dark:bg-green-950 dark:border-green-800">
                    <ShieldCheck className="h-3 w-3 mr-1" />
                    Payroll Verified
                  </Badge>
                )}
                {request.payroll_status === 'rolled_back' && (
                  <Badge variant="outline" className="text-orange-600 border-orange-300 bg-orange-50 dark:bg-orange-950 dark:border-orange-800">
                    <RotateCcw className="h-3 w-3 mr-1" />
                    Rolled Back
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Reason */}
          {request.reason && (
            <div>
              <span className="text-sm text-muted-foreground">Reason</span>
              <div className="flex items-start gap-2 mt-1">
                <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                <p className="text-sm">{request.reason}</p>
              </div>
            </div>
          )}

          {/* Submitted Date */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              Submitted: {format(new Date(request.created_at), 'MMM d, yyyy')}
            </span>
          </div>

          {/* Actions section */}
          {showActions && (
            <div className="pt-3 border-t">
              <div className="space-y-2">
                {/* Review button for pending requests */}
                {request.status === 'pending' && onStatusUpdate && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onStatusUpdate(request.id, 'approved')}
                    className="w-full"
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Review Request
                  </Button>
                )}
                
                {/* Edit button for all requests */}
                {onEditRequest && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onEditRequest(request)}
                    className="w-full"
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit Status
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
